import { useState, useEffect, useRef, type RefObject } from 'react';
import scrollTo from '@/modules/utils/scrollTo';

import {
  Text,
  TokenTextAppearance,
  TokenTextColor,
  TextTag,
} from '@volkswagen-onehub/components-core';

import {
  Border,
  BorderHighlight,
  SideTabsWrapper,
  Tab,
  TabsWrapper,
} from './SideTabs.styles';
import useGlobalStore, {
  GlobalStoreStateConfiguration,
} from '@/stores/globalStore';
import { performVWBasicButtonClick } from '@/analytics/events';
import { SectionNames } from '@/analytics/sectionNames';
import { useTribalContext } from '@/context/tribal';
import useResizeResponsive from '@/hooks/useResizeResponsive';
import { isLargeDesktop } from '@/modules/utils/breakpoints';

interface Props {
  tabs: TabProps[];
  sections: RefObject<Element[]>;
  activeConfiguration?: GlobalStoreStateConfiguration;
}

export type TabProps = {
  label: string;
  id: string;
};

function SideTabs(props: Props) {
  const {
    tabs,
    sections: { current: sectionRefs },
    activeConfiguration,
  } = props;

  const { retailers } = useGlobalStore((state) => state.app);
  const { analyticsManager, analyticsBuilder } = useTribalContext();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [borderYVal, setBorderYVal] = useState(0);
  const tabsEls = useRef(new Array());
  const isLargeDesktopWidth = useResizeResponsive(isLargeDesktop);

  const handleActiveTab = (index: number) => {
    setActiveTabIndex(index);
    setBorderPosition(index);
  };

  const setBorderPosition = (index: number) => {
    const activeTab = tabsEls.current[index];
    setBorderYVal(activeTab.offsetTop);
  };

  const handleTabClick = (id: string, label: string) => {
    scrollTo(id, 240);
    performVWBasicButtonClick({
      label,
      sectionName: SectionNames.SINGLE_PAGE_MENU,
      configuration: activeConfiguration,
      retailers,
      analyticsManager,
      analyticsBuilder,
    });
  };

  useEffect(() => {
    let currentSectionIndex: number | null = null;
    let tabIndex: number | null = null;

    // Restrics the intersection area to top half or 30% of screen.
    const options = {
      rootMargin: `0px 0px ${isLargeDesktopWidth ? '-70%' : '-50%'} 0px`,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!sectionRefs) return;

        // top position of entry
        const boundingClientRectY = entry.boundingClientRect.top;
        // top of page
        const rootBoundsY = entry.rootBounds?.top ?? 0;

        // If top of element is in viewport
        if (boundingClientRectY >= rootBoundsY) {
          currentSectionIndex = sectionRefs.indexOf(entry.target);

          // If top of next section enters intersection point, go to next tab.
          if (entry.isIntersecting) {
            tabIndex = sectionRefs.indexOf(entry.target);
            // If the top of current section exits intersection point, go to previous tab.
          } else if (currentSectionIndex === tabIndex && tabIndex !== 0) {
            tabIndex -= 1;
          } else {
            tabIndex = null;
          }

          if (tabIndex !== null) handleActiveTab(tabIndex);
        }
      });
    }, options);

    if (sectionRefs) {
      sectionRefs.forEach((section) => section && observer.observe(section));
    }

    return () => {
      if (sectionRefs)
        sectionRefs.forEach((section) => {
          if (section) observer.unobserve(section);
        });
    };
  }, [activeConfiguration, isLargeDesktopWidth]);

  return (
    <SideTabsWrapper>
      <Border>
        <BorderHighlight yVal={borderYVal} />
      </Border>
      <TabsWrapper>
        {tabs.map((tab, index) => (
          <Tab
            selected={index === activeTabIndex}
            key={index}
            ref={(el) => (tabsEls.current[index] = el)}
            onClick={() => handleTabClick(tab.id, tab.label)}
          >
            <Text
              tag={TextTag.span}
              appearance={TokenTextAppearance.headline200}
              color={TokenTextColor.inherit}
            >
              {tab.label}
            </Text>
          </Tab>
        ))}
      </TabsWrapper>
    </SideTabsWrapper>
  );
}

export default SideTabs;
